import React, { useState, useEffect, useCallback, useRef } from 'react';
import { dispatchesApi, AbortLine, ListResponse, Dispatch, DispatchStep, Escalation, StalledDispatch, BlockedOnWitness, SeatMandate } from '../api/dispatches';

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  claimed: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-indigo-100 text-indigo-800',
  blocked: 'bg-red-100 text-red-800',
  complete: 'bg-green-100 text-green-800',
  superseded: 'bg-gray-100 text-gray-500',
  draft: 'bg-gray-100 text-gray-600',
  rejected: 'bg-red-100 text-red-500',
  withdrawn: 'bg-gray-100 text-gray-400',
};

const GRADE_COLORS: Record<string, string> = {
  pass: 'text-green-700 bg-green-50',
  fail: 'text-red-700 bg-red-50',
  ambiguous: 'text-amber-700 bg-amber-50',
};

const VERDICT_COLORS: Record<string, string> = {
  proceed: 'bg-green-600 hover:bg-green-700',
  reject: 'bg-red-600 hover:bg-red-700',
  amend: 'bg-amber-600 hover:bg-amber-700',
};

const TIER_COLORS: Record<string, string> = {
  read_only: 'bg-gray-100 text-gray-700',
  write_local: 'bg-blue-100 text-blue-700',
  write_shared: 'bg-indigo-100 text-indigo-700',
  publish: 'bg-amber-100 text-amber-800',
  production: 'bg-red-100 text-red-800',
};

const STEP_STATUS_COLORS: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-600',
  released: 'bg-blue-100 text-blue-700',
  claimed: 'bg-blue-100 text-blue-800',
  reported: 'bg-indigo-100 text-indigo-700',
  graded: 'bg-amber-100 text-amber-700',
  passed: 'bg-green-100 text-green-700',
  escalated: 'bg-red-100 text-red-700',
  failed: 'bg-red-100 text-red-800',
  human_released: 'bg-green-100 text-green-800',
};

const ACTIVE_STATUSES = ['pending', 'claimed', 'in_progress', 'blocked'];

const CANONICAL_SEATS = ['james', 'jarvis', 'happyhippo', 'aegis', 'morey', 'mindmeld-coding', 'equilateral-agents', 'patent-forge', 'raia', 'witness'];

type TabKey = 'dispatches' | 'stalled' | 'blocked_witness' | 'escalations' | 'mandates';

function daysSince(dateStr: string): number {
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
}

function hoursSince(dateStr: string): string {
  const h = Math.floor((Date.now() - new Date(dateStr).getTime()) / 3600000);
  if (h < 1) return '<1h';
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

function formatAbortLine(line: AbortLine): string {
  if (typeof line === 'string') return line;
  return Object.entries(line).map(([key, value]) =>
    `${key}: ${typeof value === 'string' ? value : JSON.stringify(value)}`
  ).join('\n');
}

function isQuestionShaped(config: Escalation['config']): boolean {
  const abort = config?.abort_lines || [];
  return abort.some(line =>
    /stop\s+and\s+report/i.test(formatAbortLine(line)) ||
    /ask\s+(the\s+)?human/i.test(formatAbortLine(line)) ||
    /escalate.*question/i.test(formatAbortLine(line))
  );
}

function extractFailedPredicates(esc: Escalation): string[] {
  const predicates: string[] = [];
  const evidence = esc.grade_evidence;
  if (!evidence) return predicates;

  if (evidence.abort_line_triggered) {
    predicates.push(`Abort line triggered: "${evidence.abort_line_triggered}"`);
  }
  if (evidence.failed_checks && Array.isArray(evidence.failed_checks)) {
    for (const check of evidence.failed_checks) {
      predicates.push(typeof check === 'string' ? check : JSON.stringify(check));
    }
  }
  if (evidence.schema_violations && Array.isArray(evidence.schema_violations)) {
    for (const v of evidence.schema_violations) {
      predicates.push(`Schema: ${typeof v === 'string' ? v : JSON.stringify(v)}`);
    }
  }
  if (evidence.scope_violations && Array.isArray(evidence.scope_violations)) {
    for (const s of evidence.scope_violations) {
      predicates.push(`Scope: ${typeof s === 'string' ? s : JSON.stringify(s)}`);
    }
  }
  if (predicates.length === 0 && esc.grade === 'fail') {
    predicates.push('Grade: fail (no structured predicates recorded)');
  }
  return predicates;
}

// ── Escalation Detail Panel ────────────────────────────────────────

export const EscalationDetail: React.FC<{
  esc: Escalation;
  onResolve: () => void;
  onClose: () => void;
}> = ({ esc, onResolve, onClose }) => {
  const witnessDispute = /^Witness dispute by ([^:]+):\s*([\s\S]*)$/.exec(esc.grade_reasoning || '');
  const [verdict, setVerdict] = useState<string>('');
  const [reason, setReason] = useState(witnessDispute?.[2] || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [fidoStatus, setFidoStatus] = useState<'idle' | 'checking' | 'required' | 'optional' | 'off'>('idle');

  const questionShaped = isQuestionShaped(esc.config);
  const failedPredicates = extractFailedPredicates(esc);
  const isResolved = esc.human_verdict !== null && esc.status !== 'escalated';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setFidoStatus('checking');
      try {
        const res = await dispatchesApi.fidoConfig();
        if (cancelled) return;
        if (res.success && res.data?.config) {
          const req = res.data.config[esc.consequence_tier] || 'off';
          setFidoStatus(req as 'required' | 'optional' | 'off');
        } else {
          setFidoStatus('off');
        }
      } catch {
        if (!cancelled) setFidoStatus('off');
      }
    })();
    return () => { cancelled = true; };
  }, [esc.consequence_tier]);

  const handleSubmit = async () => {
    if (!verdict) { setError('Select a verdict'); return; }
    if (!reason.trim()) { setError('Reason is mandatory'); return; }
    setSubmitting(true);
    setError('');

    let fidoAssertion = undefined;

    if (fidoStatus === 'required' || fidoStatus === 'optional') {
      try {
        const optionsRes = await dispatchesApi.fidoAssertOptions({
          execution_context: {
            execution_id: esc.execution_id,
            dispatch_key: esc.dispatch_key,
            step_seq: esc.sequence,
            tier: esc.consequence_tier,
          },
        });

        if (!optionsRes.success || !optionsRes.data?.options) {
          if (fidoStatus === 'required') {
            setError('FIDO assertion required but failed to get options');
            setSubmitting(false);
            return;
          }
        } else {
          const opts = optionsRes.data.options;
          const credential = await navigator.credentials.get({
            publicKey: {
              challenge: Uint8Array.from(atob(opts.challenge.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0)),
              rpId: opts.rpId,
              allowCredentials: (opts.allowCredentials || []).map(c => ({
                id: Uint8Array.from(atob(c.id.replace(/-/g, '+').replace(/_/g, '/')), ch => ch.charCodeAt(0)),
                type: c.type,
                transports: c.transports,
              })),
              timeout: opts.timeout || 120000,
              userVerification: opts.userVerification || 'preferred',
            },
          }) as PublicKeyCredential | null;

          if (credential) {
            const response = credential.response as AuthenticatorAssertionResponse;
            const toBase64Url = (buf: ArrayBuffer) => {
              const bytes = new Uint8Array(buf);
              let binary = '';
              bytes.forEach(b => { binary += String.fromCharCode(b); });
              return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
            };
            fidoAssertion = {
              id: credential.id,
              rawId: toBase64Url(credential.rawId),
              type: credential.type,
              response: {
                authenticatorData: toBase64Url(response.authenticatorData),
                clientDataJSON: toBase64Url(response.clientDataJSON),
                signature: toBase64Url(response.signature),
                userHandle: response.userHandle ? toBase64Url(response.userHandle) : null,
              },
              clientExtensionResults: credential.getClientExtensionResults(),
            };
          } else if (fidoStatus === 'required') {
            setError('FIDO assertion is required for this tier');
            setSubmitting(false);
            return;
          }
        }
      } catch (err) {
        if (fidoStatus === 'required') {
          setError(`FIDO assertion failed: ${err instanceof Error ? err.message : 'unknown error'}`);
          setSubmitting(false);
          return;
        }
      }
    }

    const res = await dispatchesApi.resolveEscalation({
      execution_id: esc.execution_id,
      human_verdict: verdict,
      verdict_reason: reason.trim(),
      fido_assertion: fidoAssertion,
    });
    setSubmitting(false);
    if (res.success) {
      onResolve();
    } else {
      setError(res.error || 'Failed to resolve');
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {esc.dispatch_key} -- Step {esc.sequence}: {esc.step_name}
          </h3>
          <div className="flex gap-3 mt-1 text-xs text-gray-500">
            <span>Tier: <span className="font-medium">{esc.consequence_tier}</span></span>
            <span>Grader: <span className="font-medium">{esc.grader_mode}</span></span>
            <span>Seat: <span className="font-medium">{esc.assigned_seat}</span></span>
            {esc.graded_at && <span>Waiting: <span className="font-medium text-red-600">{hoursSince(esc.graded_at)}</span></span>}
          </div>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
      </div>

      <div className="p-5 space-y-5">
        {witnessDispute && (
          <section aria-label="Witness dispute" className="border border-amber-300 bg-amber-50 rounded p-4">
            <h4 className="text-sm font-semibold text-amber-900">Witness dispute</h4>
            <p className="text-xs text-amber-800 mt-1">Witness: {witnessDispute[1]} · Verdict: dispute</p>
            <p className="text-sm text-gray-800 whitespace-pre-wrap mt-2">{witnessDispute[2]}</p>
            <p className="text-xs text-gray-600 mt-2">Review these findings against the executor report below. Uphold returns the step for amendment; overrule allows it to proceed.</p>
          </section>
        )}
        {/* Step Config */}
        <section>
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Step Config</h4>
          <div className="bg-gray-50 border border-gray-200 rounded p-4 space-y-3">
            {esc.config?.instruction && (
              <div>
                <div className="text-xs font-medium text-gray-500 mb-1">Instruction</div>
                <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono leading-relaxed">{esc.config.instruction}</pre>
              </div>
            )}
            {esc.config?.abort_lines && esc.config.abort_lines.length > 0 && (
              <div>
                <div className="text-xs font-medium text-gray-500 mb-1">Abort Lines</div>
                <ul className="space-y-1">
                  {esc.config.abort_lines.map((line, i) => (
                    <li key={i} className="text-sm font-mono text-red-700 bg-red-50 px-2 py-1 rounded whitespace-pre-wrap">{formatAbortLine(line)}</li>
                  ))}
                </ul>
              </div>
            )}
            {esc.config && Object.keys(esc.config).filter(k => k !== 'instruction' && k !== 'abort_lines').length > 0 && (
              <div>
                <div className="text-xs font-medium text-gray-500 mb-1">Other Config</div>
                <pre className="text-xs text-gray-600 whitespace-pre-wrap font-mono">
                  {JSON.stringify(
                    Object.fromEntries(Object.entries(esc.config).filter(([k]) => k !== 'instruction' && k !== 'abort_lines')),
                    null, 2
                  )}
                </pre>
              </div>
            )}
          </div>
        </section>

        {/* Report */}
        <section>
          <div className="flex items-center gap-2 mb-2">
            <h4 className="text-sm font-semibold text-gray-700">Report</h4>
            {questionShaped && (
              <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                Question-shaped
              </span>
            )}
          </div>
          {questionShaped && (
            <p className="text-xs text-purple-700 bg-purple-50 border border-purple-200 rounded px-3 py-2 mb-2">
              This report satisfies a STOP AND REPORT condition in the step config.
              It is a question to answer, not a completion to grade pass/fail.
            </p>
          )}
          {esc.report ? (
            <pre className="bg-gray-50 border border-gray-200 rounded p-4 text-sm text-gray-800 whitespace-pre-wrap font-mono leading-relaxed overflow-x-auto max-h-96 overflow-y-auto">
              {typeof esc.report === 'string' ? esc.report : JSON.stringify(esc.report, null, 2)}
            </pre>
          ) : (
            <p className="text-sm text-gray-400 italic">No report submitted</p>
          )}
        </section>

        {/* Grade Details */}
        <section>
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Grade</h4>
          <div className="flex items-start gap-4">
            <div>
              <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${GRADE_COLORS[esc.grade || ''] || 'bg-gray-100 text-gray-600'}`}>
                {esc.grade || 'ungraded'}
              </span>
              {esc.graded_by && (
                <div className="text-xs text-gray-500 mt-1">by {esc.graded_by}</div>
              )}
            </div>
            {esc.grade_reasoning && (
              <div className="flex-1">
                <div className="text-xs font-medium text-gray-500 mb-1">Reasoning</div>
                <p className="text-sm text-gray-700">{esc.grade_reasoning}</p>
              </div>
            )}
          </div>
          {failedPredicates.length > 0 && (
            <div className="mt-3">
              <div className="text-xs font-medium text-gray-500 mb-1">Failed Predicates (Class M)</div>
              <ul className="space-y-1">
                {failedPredicates.map((p, i) => (
                  <li key={i} className="text-sm font-mono text-red-700 bg-red-50 px-2 py-1 rounded">{p}</li>
                ))}
              </ul>
            </div>
          )}
          {esc.grade_evidence && Object.keys(esc.grade_evidence).length > 0 && (
            <details className="mt-3">
              <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">Full grade evidence</summary>
              <pre className="mt-1 text-xs text-gray-600 whitespace-pre-wrap font-mono bg-gray-50 border rounded p-3 overflow-x-auto">
                {JSON.stringify(esc.grade_evidence, null, 2)}
              </pre>
            </details>
          )}
        </section>

        {/* Resolved verdict */}
        {isResolved && (
          <section>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Verdict</h4>
            <div className="bg-gray-50 border border-gray-200 rounded p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                  esc.human_verdict === 'proceed' ? 'bg-green-100 text-green-800' :
                  esc.human_verdict === 'reject' ? 'bg-red-100 text-red-800' :
                  'bg-amber-100 text-amber-800'
                }`}>
                  {esc.human_verdict}
                </span>
                <span className="text-xs text-gray-500">by {esc.verdict_by}</span>
                {esc.verdict_at && <span className="text-xs text-gray-400">{new Date(esc.verdict_at).toLocaleString()}</span>}
              </div>
              <p className="text-sm text-gray-700">{esc.verdict_reason}</p>
            </div>
          </section>
        )}

        {/* Resolve Form */}
        {!isResolved && (
          <section>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">
              {questionShaped ? 'Answer' : 'Resolve Escalation'}
            </h4>
            <div className="space-y-3">
              <div className="flex gap-2">
                {(['proceed', 'reject', 'amend'] as const).map(v => (
                  <button
                    key={v}
                    onClick={() => setVerdict(v)}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      verdict === v
                        ? `${VERDICT_COLORS[v]} text-white`
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {v === 'proceed' ? (witnessDispute ? 'Overrule (proceed)' : questionShaped ? 'Answer provided -- proceed' : 'Proceed')
                     : v === 'reject' ? 'Reject'
                     : witnessDispute ? 'Uphold (amend)'
                     : 'Amend (re-do with feedback)'}
                  </button>
                ))}
              </div>
              <textarea
                aria-label="Verdict reason"
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder={questionShaped
                  ? 'Your answer to the question raised in the report...'
                  : 'Why this verdict? Mandatory.'}
                rows={3}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              {witnessDispute && (
                <button type="button" onClick={onClose} disabled={submitting} className="text-sm text-gray-600 underline">
                  Hold (leave pending)
                </button>
              )}
              {error && <p className="text-sm text-red-600">{error}</p>}
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSubmit}
                  disabled={submitting || !verdict || !reason.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Submitting...' : fidoStatus === 'required' ? 'Submit with FIDO' : 'Submit Verdict'}
                </button>
                {fidoStatus === 'required' && (
                  <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800">
                    FIDO required
                  </span>
                )}
                {fidoStatus === 'optional' && (
                  <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                    FIDO optional
                  </span>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Coverage limit */}
        <section className="border-t border-gray-200 pt-4">
          <p className="text-xs text-gray-400 leading-relaxed">
            <span className="font-medium text-gray-500">Coverage limit:</span> This view captures
            held steps, including grader failures and witness disputes. A passing automated grade
            does not resolve a witness dispute. This view does not establish that unchallenged
            passing reports are correct.
          </p>
        </section>
      </div>
    </div>
  );
};

// ── Assign Seat Modal ──────────────────────────────────────────────

const AssignSeatModal: React.FC<{
  dispatch: Dispatch | StalledDispatch;
  title?: string;
  onAssign: (dispatchId: string, seat: string) => Promise<void>;
  onClose: () => void;
}> = ({ dispatch, title, onAssign, onClose }) => {
  const [selectedSeat, setSelectedSeat] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pendingAction = useRef(false);

  const handleAssign = async () => {
    if (!selectedSeat) return;
    if (pendingAction.current) return;
    pendingAction.current = true;
    setSubmitting(true);
    setError(null);
    try {
      await onAssign(dispatch.dispatch_id, selectedSeat);
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Action failed');
    } finally {
      pendingAction.current = false;
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Enter' && selectedSeat) handleAssign();
      const idx = parseInt(e.key);
      if (idx >= 1 && idx <= CANONICAL_SEATS.length) {
        setSelectedSeat(CANONICAL_SEATS[idx - 1]);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selectedSeat]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label="Assign seat" className="bg-white rounded-lg shadow-xl p-5 w-96" onClick={e => e.stopPropagation()}>
        {error && <p role="alert" className="text-sm text-red-700 mb-3">{error}</p>}
        <h3 className="text-sm font-semibold text-gray-900 mb-1">{title || 'Assign Seat'}</h3>
        <p className="text-xs text-gray-500 mb-4">{dispatch.dispatch_key}</p>
        <div className="space-y-1">
          {CANONICAL_SEATS.map((seat, i) => (
            <button
              key={seat}
              onClick={() => setSelectedSeat(seat)}
              className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors flex items-center gap-2 ${
                selectedSeat === seat
                  ? 'bg-blue-100 text-blue-800 font-medium'
                  : 'hover:bg-gray-100 text-gray-700'
              }`}
            >
              <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-gray-400 bg-gray-100 rounded">
                {i + 1}
              </span>
              {seat}
            </button>
          ))}
        </div>
        <div className="flex gap-2 mt-4">
          <button
            onClick={handleAssign}
            disabled={!selectedSeat || submitting}
            className="flex-1 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? 'Assigning...' : 'Assign'}
          </button>
          <button
            onClick={onClose}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            Cancel <span className="text-xs text-gray-400">Esc</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Reject Modal ───────────────────────────────────────────────────

const RejectModal: React.FC<{
  dispatch: Dispatch;
  onReject: (dispatchId: string, reason: string) => Promise<void>;
  onClose: () => void;
}> = ({ dispatch, onReject, onClose }) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pendingAction = useRef(false);

  const handleReject = async () => {
    if (!reason.trim()) return;
    if (pendingAction.current) return;
    pendingAction.current = true;
    setSubmitting(true);
    setError(null);
    try {
      await onReject(dispatch.dispatch_id, reason.trim());
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Action failed');
    } finally {
      pendingAction.current = false;
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label="Reject dispatch" className="bg-white rounded-lg shadow-xl p-5 w-96" onClick={e => e.stopPropagation()}>
        {error && <p role="alert" className="text-sm text-red-700 mb-3">{error}</p>}
        <h3 className="text-sm font-semibold text-gray-900 mb-1">Reject Dispatch</h3>
        <p className="text-xs text-gray-500 mb-4">{dispatch.dispatch_key}</p>
        <textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="Reason for rejection (required)"
          rows={3}
          autoFocus
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
        />
        <div className="flex gap-2 mt-3">
          <button
            onClick={handleReject}
            disabled={!reason.trim() || submitting}
            className="flex-1 px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            {submitting ? 'Rejecting...' : 'Reject'}
          </button>
          <button
            onClick={onClose}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            Cancel <span className="text-xs text-gray-400">Esc</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Withdraw Modal ─────────────────────────────────────────────────

const WithdrawModal: React.FC<{
  dispatch: Dispatch | StalledDispatch;
  onWithdraw: (dispatchId: string, reason: string) => Promise<void>;
  onClose: () => void;
}> = ({ dispatch, onWithdraw, onClose }) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pendingAction = useRef(false);

  const handleWithdraw = async () => {
    if (!reason.trim()) return;
    if (pendingAction.current) return;
    pendingAction.current = true;
    setSubmitting(true);
    setError(null);
    try {
      await onWithdraw(dispatch.dispatch_id, reason.trim());
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Action failed');
    } finally {
      pendingAction.current = false;
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label="Withdraw dispatch" className="bg-white rounded-lg shadow-xl p-5 w-96" onClick={e => e.stopPropagation()}>
        {error && <p role="alert" className="text-sm text-red-700 mb-3">{error}</p>}
        <h3 className="text-sm font-semibold text-red-800 mb-1">Withdraw Dispatch</h3>
        <p className="text-xs text-gray-500 mb-1">{dispatch.dispatch_key}</p>
        <p className="text-xs text-red-600 mb-4">This is a terminal state. The dispatch cannot be re-opened.</p>
        <textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="Reason for withdrawal (required)"
          rows={3}
          autoFocus
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
        />
        <div className="flex gap-2 mt-3">
          <button
            onClick={handleWithdraw}
            disabled={!reason.trim() || submitting}
            className="flex-1 px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            {submitting ? 'Withdrawing...' : 'Withdraw'}
          </button>
          <button
            onClick={onClose}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            Cancel <span className="text-xs text-gray-400">Esc</span>
          </button>
        </div>
      </div>
    </div>
  );
};


// ── Complete Modal ────────────────────────────────────────────────

const CompleteModal: React.FC<{
  dispatch: Dispatch;
  allStepsPassed: boolean;
  onComplete: (dispatchId: string, completionRef: string) => Promise<void>;
  onClose: () => void;
}> = ({ dispatch, allStepsPassed, onComplete, onClose }) => {
  const [completionRef, setCompletionRef] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pendingAction = useRef(false);

  const handleComplete = async () => {
    if (pendingAction.current) return;
    pendingAction.current = true;
    setSubmitting(true);
    setError(null);
    try {
      await onComplete(dispatch.dispatch_id, completionRef.trim());
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Action failed');
    } finally {
      pendingAction.current = false;
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label="Complete dispatch" className="bg-white rounded-lg shadow-xl p-5 w-96" onClick={e => e.stopPropagation()}>
        {error && <p role="alert" className="text-sm text-red-700 mb-3">{error}</p>}
        <h3 className="text-sm font-semibold text-green-800 mb-1">Complete Dispatch</h3>
        <p className="text-xs text-gray-500 mb-1">{dispatch.dispatch_key}</p>
        {!allStepsPassed && (
          <p className="text-xs text-amber-600 mb-3">Not all steps have passed. Are you sure?</p>
        )}
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-500 mb-1">Completion ref (document ID, optional)</label>
          <input
            type="text"
            value={completionRef}
            onChange={e => setCompletionRef(e.target.value)}
            placeholder="e.g. a1b2c3d4-..."
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500 font-mono"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleComplete}
            disabled={submitting}
            className="flex-1 px-3 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
          >
            {submitting ? 'Completing...' : 'Complete'}
          </button>
          <button
            onClick={onClose}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            Cancel <span className="text-xs text-gray-400">Esc</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Dispatch Detail Panel ─────────────────────────────────────────

const DispatchDetail: React.FC<{
  dispatch: Dispatch;
  onClose: () => void;
  onReassign: (d: Dispatch) => void;
  onWithdraw: (d: Dispatch) => void;
  onComplete: (d: Dispatch, allStepsPassed: boolean) => void;
  onRefresh: () => void;
}> = ({ dispatch, onClose, onReassign, onWithdraw, onComplete, onRefresh }) => {
  const [steps, setSteps] = useState<DispatchStep[]>([]);
  const [documentAbstract, setDocumentAbstract] = useState<string | null>(null);
  const [loadingSteps, setLoadingSteps] = useState(true);
  const [expandedStep, setExpandedStep] = useState<string | null>(null);
  const [actionError, setActionError] = useState('');
  const [blockerStatuses, setBlockerStatuses] = useState<Record<string, { status: string; steps_complete: number; step_count: number }>>({});

  useEffect(() => {
    setLoadingSteps(true);
    dispatchesApi.steps(dispatch.dispatch_id).then(res => {
      if (res.success && res.data) {
        setSteps(res.data.steps || []);
        setDocumentAbstract(res.data.document_abstract);
      }
      setLoadingSteps(false);
    });
  }, [dispatch.dispatch_id]);

  useEffect(() => {
    if (!dispatch.blocked_on || dispatch.blocked_on.length === 0) return;
    dispatch.blocked_on.forEach(key => {
      dispatchesApi.list({ dispatch_key: key }).then(res => {
        if (res.success && res.data && res.data.records.length > 0) {
          const d = res.data.records[0];
          setBlockerStatuses(prev => ({ ...prev, [key]: { status: d.status, steps_complete: d.steps_complete || 0, step_count: d.step_count || 0 } }));
        }
      });
    });
  }, [dispatch.blocked_on]);

  const handleBlock = async () => {
    setActionError('');
    const res = await dispatchesApi.action({
      action: 'update_status',
      dispatch_id: dispatch.dispatch_id,
      status: 'blocked',
    });
    if (res.success) { onRefresh(); } else { setActionError(res.error || 'Failed'); }
  };

  const handleUnblock = async () => {
    setActionError('');
    const res = await dispatchesApi.action({
      action: 'update_status',
      dispatch_id: dispatch.dispatch_id,
      status: 'pending',
    });
    if (res.success) { onRefresh(); } else { setActionError(res.error || 'Failed'); }
  };

  const isBlocked = dispatch.status === 'blocked';
  const isTerminal = ['complete', 'superseded', 'rejected', 'withdrawn'].includes(dispatch.status);
  const allStepsPassed = steps.length > 0 && steps.every(s => s.execution_status === 'passed' || s.execution_status === 'human_released');

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{dispatch.dispatch_key}</h3>
          <div className="flex gap-3 mt-1 text-xs text-gray-500">
            <span className={`inline-flex px-2 py-0.5 font-medium rounded-full ${STATUS_COLORS[dispatch.status] || ''}`}>
              {dispatch.status}
            </span>
            <span>Seat: <span className="font-medium">{dispatch.assigned_seat}</span></span>
            {dispatch.claimed_by && <span>Claimed: <span className="font-medium">{dispatch.claimed_by}</span></span>}
            <span>Age: <span className="font-medium">{daysSince(dispatch.created_at)}d</span></span>
          </div>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
      </div>

      <div className="p-5 space-y-5">
        {/* Document abstract */}
        {documentAbstract && (
          <section>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Linked Document</h4>
            <div className="bg-gray-50 border border-gray-200 rounded p-3">
              <div className="text-xs font-medium text-gray-500 mb-1">{dispatch.document_name}</div>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{documentAbstract}</p>
            </div>
          </section>
        )}

        {/* Blocked On */}
        {dispatch.blocked_on && dispatch.blocked_on.length > 0 && (
          <section>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Blocked On</h4>
            <div className="space-y-1.5">
              {dispatch.blocked_on.map(key => {
                const info = blockerStatuses[key];
                const isComplete = info?.status === 'complete';
                const isTerminalBlocker = info && ['complete', 'superseded', 'rejected', 'withdrawn'].includes(info.status);
                return (
                  <div key={key} className={`flex items-center justify-between px-3 py-2 rounded-md border ${isComplete ? 'bg-green-50 border-green-200' : isTerminalBlocker ? 'bg-gray-50 border-gray-200' : 'bg-amber-50 border-amber-200'}`}>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${isComplete ? 'bg-green-500' : isTerminalBlocker ? 'bg-gray-400' : 'bg-amber-500'}`} />
                      <span className="text-sm font-medium text-gray-900">{key}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {info ? (
                        <>
                          <span className="text-xs text-gray-500">{info.steps_complete}/{info.step_count} steps</span>
                          <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_COLORS[info.status] || 'bg-gray-100 text-gray-800'}`}>
                            {info.status}
                          </span>
                        </>
                      ) : (
                        <span className="text-xs text-gray-400">loading...</span>
                      )}
                    </div>
                  </div>
                );
              })}
              {Object.keys(blockerStatuses).length === (dispatch.blocked_on?.length || 0) &&
               dispatch.blocked_on.every(key => {
                 const info = blockerStatuses[key];
                 return info && ['complete', 'superseded', 'rejected', 'withdrawn'].includes(info.status);
               }) && (
                <p className="text-xs text-green-700 mt-1 font-medium">All blockers resolved — safe to unblock.</p>
              )}
            </div>
          </section>
        )}

        {/* Actions */}
        {!isTerminal && (
          <section>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Actions</h4>
            <div className="flex gap-2">
              <button
                onClick={() => onComplete(dispatch, allStepsPassed)}
                className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                  allStepsPassed
                    ? 'text-white bg-green-600 hover:bg-green-700'
                    : 'text-green-700 bg-green-50 hover:bg-green-100'
                }`}
              >
                Complete
              </button>
              <button
                onClick={() => onReassign(dispatch)}
                className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded hover:bg-blue-100 transition-colors"
              >
                Reassign
              </button>
              {isBlocked ? (
                <button
                  onClick={handleUnblock}
                  className="px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 rounded hover:bg-green-100 transition-colors"
                >
                  Unblock
                </button>
              ) : (
                <button
                  onClick={handleBlock}
                  className="px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 rounded hover:bg-amber-100 transition-colors"
                >
                  Block
                </button>
              )}
              <button
                onClick={() => onWithdraw(dispatch)}
                className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded hover:bg-red-100 transition-colors"
              >
                Withdraw
              </button>
            </div>
            {actionError && <p className="text-xs text-red-600 mt-1">{actionError}</p>}
          </section>
        )}

        {/* Steps */}
        <section>
          <h4 className="text-sm font-semibold text-gray-700 mb-2">
            Steps ({steps.length})
          </h4>
          {loadingSteps ? (
            <div className="text-sm text-gray-400 py-4 text-center">Loading steps...</div>
          ) : steps.length === 0 ? (
            <div className="text-sm text-gray-400 py-4 text-center">No steps defined</div>
          ) : (
            <div className="space-y-1">
              {steps.map(step => {
                const isExpanded = expandedStep === step.step_id;
                const execStatus = step.execution_status || 'pending';
                const hasConfig = step.config !== null;
                return (
                  <div key={step.step_id} className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setExpandedStep(isExpanded ? null : step.step_id)}
                      className="w-full text-left flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-mono text-gray-400 w-5">{step.sequence}</span>
                        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${STEP_STATUS_COLORS[execStatus] || 'bg-gray-100 text-gray-600'}`}>
                          {execStatus}
                        </span>
                        <span className="text-sm font-medium text-gray-900">{step.name}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span className={`px-1.5 py-0.5 rounded ${TIER_COLORS[step.consequence_tier] || ''}`}>
                          {step.consequence_tier}
                        </span>
                        <span>{step.grader_mode}</span>
                        {step.grader_mode === 'M_plus_witness' && (
                          <span className={`px-1.5 py-0.5 rounded text-xs ${
                            execStatus === 'graded' ? 'bg-amber-100 text-amber-800' :
                            execStatus === 'passed' ? 'bg-green-100 text-green-800' :
                            execStatus === 'escalated' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {execStatus === 'graded' ? '👁 Awaiting Witness' :
                             execStatus === 'passed' ? '✓ Witnessed' :
                             execStatus === 'escalated' ? '⚠ Disputed' : ''}
                          </span>
                        )}
                        {step.grade && (
                          <span className={`px-1.5 py-0.5 rounded-full ${GRADE_COLORS[step.grade] || ''}`}>
                            {step.grade}
                          </span>
                        )}
                        <span className="text-gray-400">{isExpanded ? '▲' : '▼'}</span>
                      </div>
                    </button>
                    {isExpanded && (
                      <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/50 space-y-3">
                        {/* Step config — only for released/reported steps */}
                        {hasConfig && step.config?.instruction && (
                          <div>
                            <div className="text-xs font-medium text-gray-500 mb-1">Instruction</div>
                            <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono leading-relaxed bg-white border border-gray-200 rounded p-3 max-h-64 overflow-y-auto">
                              {step.config.instruction}
                            </pre>
                          </div>
                        )}
                        {hasConfig && step.config?.abort_lines && step.config.abort_lines.length > 0 && (
                          <div>
                            <div className="text-xs font-medium text-gray-500 mb-1">Abort Lines</div>
                            <ul className="space-y-1">
                              {step.config.abort_lines.map((line, i) => (
                                <li key={i} className="text-xs font-mono text-red-700 bg-red-50 px-2 py-1 rounded whitespace-pre-wrap">{formatAbortLine(line)}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {!hasConfig && execStatus === 'pending' && (
                          <p className="text-xs text-gray-400 italic">Config hidden for unreleased steps</p>
                        )}

                        {/* Execution metadata */}
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                          {step.claimed_seat && <span>Seat: <span className="font-medium">{step.claimed_seat}</span></span>}
                          {step.step_claimed_by && <span>Claimed by: <span className="font-medium">{step.step_claimed_by}</span></span>}
                          {step.released_at && <span>Released: {new Date(step.released_at).toLocaleString()}</span>}
                          {step.reported_at && <span>Reported: {new Date(step.reported_at).toLocaleString()}</span>}
                          {step.graded_at && <span>Graded: {new Date(step.graded_at).toLocaleString()}</span>}
                          {step.human_verdict && (
                            <span>
                              Verdict: <span className={`font-medium ${step.human_verdict === 'proceed' ? 'text-green-700' : step.human_verdict === 'reject' ? 'text-red-700' : 'text-amber-700'}`}>
                                {step.human_verdict}
                              </span>
                            </span>
                          )}
                          {step.verdict_reason && <span className="text-gray-400">({step.verdict_reason})</span>}
                        </div>

                        {/* Report */}
                        {step.report && (
                          <div>
                            <div className="text-xs font-medium text-gray-500 mb-1">Report</div>
                            <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono leading-relaxed bg-white border border-gray-200 rounded p-3 max-h-64 overflow-y-auto overflow-x-auto">
                              {typeof step.report === 'string' ? step.report : JSON.stringify(step.report, null, 2)}
                            </pre>
                          </div>
                        )}

                        {/* Grade */}
                        {step.grade && (
                          <div className="flex items-start gap-3">
                            <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${GRADE_COLORS[step.grade] || 'bg-gray-100 text-gray-600'}`}>
                              {step.grade}
                            </span>
                            {step.grade_reasoning && (
                              <p className="text-xs text-gray-600">{step.grade_reasoning}</p>
                            )}
                          </div>
                        )}

                        {/* Release condition */}
                        {step.release_condition && Object.keys(step.release_condition).length > 0 && (
                          <details>
                            <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">Release condition</summary>
                            <pre className="mt-1 text-xs text-gray-600 whitespace-pre-wrap font-mono bg-white border rounded p-2 overflow-x-auto">
                              {JSON.stringify(step.release_condition, null, 2)}
                            </pre>
                          </details>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

// ── Dispatch Table ─────────────────────────────────────────────────

const DispatchTable: React.FC<{
  dispatches: Dispatch[];
  showActions?: boolean;
  onAssignSeat?: (d: Dispatch) => void;
  onReject?: (d: Dispatch) => void;
  onSelect?: (d: Dispatch) => void;
}> = ({ dispatches, showActions, onAssignSeat, onReject, onSelect }) => {
  if (dispatches.length === 0) {
    return <div className="text-gray-500 text-sm py-4 text-center">No dispatches</div>;
  }

  return (
    <div className="bg-white shadow-sm rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dispatch</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Seat</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Claimed By</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Steps</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Age</th>
              {showActions && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {dispatches.map(d => {
              const age = daysSince(d.created_at);
              const isOld = ACTIVE_STATUSES.includes(d.status) && age > 7;
              return (
                <tr
                  key={d.dispatch_id}
                  className={`${isOld ? 'bg-red-50' : ''} ${onSelect ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                  onClick={() => onSelect?.(d)}
                >
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900">{d.dispatch_key}</div>
                    {d.document_name && (
                      <div className="text-xs text-gray-500">{d.document_name}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_COLORS[d.status] || 'bg-gray-100 text-gray-800'}`}>
                      {d.status}
                    </span>
                    {d.blocked_on && d.blocked_on.length > 0 && (
                      <span className="ml-1 text-xs text-gray-400" title={d.blocked_on.join(', ')}>
                        ({d.blocked_on.length} dep{d.blocked_on.length !== 1 ? 's' : ''})
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{d.assigned_seat}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{d.claimed_by || '--'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {d.step_count != null ? (
                      <span className={d.steps_complete === d.step_count && d.step_count > 0 ? 'text-green-700 font-medium' : ''}>
                        {d.steps_complete || 0}/{d.step_count}
                      </span>
                    ) : '--'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className={isOld ? 'text-red-700 font-medium' : 'text-gray-500'}>
                      {age}d
                    </span>
                  </td>
                  {showActions && (
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); onAssignSeat?.(d); }}
                          className="px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 rounded hover:bg-blue-100 transition-colors"
                          title="Assign to seat (a)"
                        >
                          Assign
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); onReject?.(d); }}
                          className="px-2 py-1 text-xs font-medium text-red-700 bg-red-50 rounded hover:bg-red-100 transition-colors"
                          title="Reject dispatch (x)"
                        >
                          Reject
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Stalled Dispatches Table ───────────────────────────────────────

const StalledTable: React.FC<{
  items: StalledDispatch[];
  onClearClaim: (d: StalledDispatch) => void;
  onReassignSeat: (d: StalledDispatch) => void;
  onWithdraw: (d: StalledDispatch) => void;
}> = ({ items, onClearClaim, onReassignSeat, onWithdraw }) => {
  if (items.length === 0) {
    return (
      <div className="text-gray-500 text-sm py-8 text-center bg-white rounded-lg shadow-sm">
        No stalled dispatches
      </div>
    );
  }

  return (
    <div className="bg-white shadow-sm rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dispatch</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Seat</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Claimed By</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Claimed At</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Age</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Activity</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {items.map(d => (
              <tr key={d.dispatch_id} className="bg-amber-50/30">
                <td className="px-4 py-3">
                  <div className="text-sm font-medium text-gray-900">{d.dispatch_key}</div>
                  {d.document_name && (
                    <div className="text-xs text-gray-500">{d.document_name}</div>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{d.assigned_seat}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{d.claimed_by || '--'}</td>
                <td className="px-4 py-3 text-sm text-gray-500">
                  {d.claimed_at ? new Date(d.claimed_at).toLocaleDateString() : '--'}
                </td>
                <td className="px-4 py-3 text-sm">
                  <span className="text-amber-700 font-medium">
                    {Math.floor(d.age_days)}d
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">
                  {d.last_step_activity && d.last_step_activity !== '1970-01-01T00:00:00.000Z'
                    ? hoursSince(d.last_step_activity) + ' ago'
                    : 'none'}
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    {d.claimed_by && (
                      <button
                        onClick={() => onClearClaim(d)}
                        className="px-2 py-1 text-xs font-medium text-amber-700 bg-amber-50 rounded hover:bg-amber-100 transition-colors"
                        title="Clear claim and return to pending"
                      >
                        Clear Claim
                      </button>
                    )}
                    <button
                      onClick={() => onReassignSeat(d)}
                      className="px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 rounded hover:bg-blue-100 transition-colors"
                      title="Reassign to different seat"
                    >
                      Reassign
                    </button>
                    <button
                      onClick={() => onWithdraw(d)}
                      className="px-2 py-1 text-xs font-medium text-red-700 bg-red-50 rounded hover:bg-red-100 transition-colors"
                      title="Withdraw (terminal)"
                    >
                      Withdraw
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Blocked on Witness Table ───────────────────────────────────────

// ── Witness Detail Panel ──────────────────────────────────────────

const WitnessDetail: React.FC<{
  item: BlockedOnWitness;
  onRelease: () => void;
  onClose: () => void;
}> = ({ item, onRelease, onClose }) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleRelease = async () => {
    if (!reason.trim()) { setError('Reason is mandatory'); return; }
    setSubmitting(true);
    setError('');
    const res = await dispatchesApi.resolveEscalation({
      execution_id: item.execution_id,
      human_verdict: 'proceed',
      verdict_reason: reason.trim(),
    });
    setSubmitting(false);
    if (res.success) {
      onRelease();
    } else {
      setError(res.error || 'Failed to release');
    }
  };

  const handleReject = async () => {
    if (!reason.trim()) { setError('Reason is mandatory'); return; }
    setSubmitting(true);
    setError('');
    const res = await dispatchesApi.resolveEscalation({
      execution_id: item.execution_id,
      human_verdict: 'reject',
      verdict_reason: reason.trim(),
    });
    setSubmitting(false);
    if (res.success) {
      onRelease();
    } else {
      setError(res.error || 'Failed to reject');
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {item.dispatch_key} -- Step {item.sequence}: {item.step_name}
          </h3>
          <div className="flex gap-3 mt-1 text-xs text-gray-500">
            <span>Tier: <span className={`font-medium px-1.5 py-0.5 rounded ${TIER_COLORS[item.consequence_tier] || ''}`}>{item.consequence_tier}</span></span>
            <span>Grader: <span className="font-medium">{item.grader_mode}</span></span>
            <span>Seat: <span className="font-medium">{item.assigned_seat}</span></span>
            {item.graded_at && <span>Waiting: <span className="font-medium text-red-600">{hoursSince(item.graded_at)}</span></span>}
          </div>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
      </div>

      <div className="p-5 space-y-5">
        {/* Step Config */}
        <section>
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Step Config</h4>
          <div className="bg-gray-50 border border-gray-200 rounded p-4 space-y-3">
            {item.config?.instruction && (
              <div>
                <div className="text-xs font-medium text-gray-500 mb-1">Instruction</div>
                <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono leading-relaxed">{item.config.instruction}</pre>
              </div>
            )}
            {item.config?.abort_lines && item.config.abort_lines.length > 0 && (
              <div>
                <div className="text-xs font-medium text-gray-500 mb-1">Abort Lines</div>
                <ul className="space-y-1">
                  {item.config.abort_lines.map((line, i) => (
                    <li key={i} className="text-sm font-mono text-red-700 bg-red-50 px-2 py-1 rounded whitespace-pre-wrap">{formatAbortLine(line)}</li>
                  ))}
                </ul>
              </div>
            )}
            {item.config && Object.keys(item.config).filter(k => k !== 'instruction' && k !== 'abort_lines').length > 0 && (
              <div>
                <div className="text-xs font-medium text-gray-500 mb-1">Other Config</div>
                <pre className="text-xs text-gray-600 whitespace-pre-wrap font-mono">
                  {JSON.stringify(
                    Object.fromEntries(Object.entries(item.config).filter(([k]) => k !== 'instruction' && k !== 'abort_lines')),
                    null, 2
                  )}
                </pre>
              </div>
            )}
          </div>
        </section>

        {/* Report */}
        <section>
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Report</h4>
          {item.report ? (
            <pre className="bg-gray-50 border border-gray-200 rounded p-4 text-sm text-gray-800 whitespace-pre-wrap font-mono leading-relaxed overflow-x-auto max-h-96 overflow-y-auto">
              {typeof item.report === 'string' ? item.report : JSON.stringify(item.report, null, 2)}
            </pre>
          ) : (
            <p className="text-sm text-gray-400 italic">No report submitted</p>
          )}
        </section>

        {/* Grade Details */}
        <section>
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Grade</h4>
          <div className="flex items-start gap-4">
            <div>
              <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${GRADE_COLORS[item.grade || ''] || 'bg-gray-100 text-gray-600'}`}>
                {item.grade || 'ungraded'}
              </span>
              {item.graded_by && (
                <div className="text-xs text-gray-500 mt-1">by {item.graded_by}</div>
              )}
            </div>
            {item.grade_reasoning && (
              <div className="flex-1">
                <div className="text-xs font-medium text-gray-500 mb-1">Reasoning</div>
                <p className="text-sm text-gray-700">{item.grade_reasoning}</p>
              </div>
            )}
          </div>
          {item.grade_evidence && Object.keys(item.grade_evidence).length > 0 && (
            <details className="mt-3">
              <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">Full grade evidence</summary>
              <pre className="mt-1 text-xs text-gray-600 whitespace-pre-wrap font-mono bg-gray-50 border rounded p-3 overflow-x-auto">
                {JSON.stringify(item.grade_evidence, null, 2)}
              </pre>
            </details>
          )}
        </section>

        {/* Release / Reject Form */}
        <section>
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Escalation Owner Verdict</h4>
          <p className="text-xs text-gray-500 mb-3">
            This step requires human authorization before the next step releases.
            Review the report above and provide your verdict.
          </p>
          <div className="space-y-3">
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Your witness notes — what you verified, any concerns (required)"
              rows={3}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex gap-2">
              <button
                onClick={handleRelease}
                disabled={submitting || !reason.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Submitting...' : 'Release (proceed)'}
              </button>
              <button
                onClick={handleReject}
                disabled={submitting || !reason.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reject
              </button>
            </div>
          </div>
        </section>

        {/* Release condition */}
        {item.release_condition && Object.keys(item.release_condition).length > 0 && (
          <section className="border-t border-gray-200 pt-4">
            <details>
              <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">Release condition</summary>
              <pre className="mt-1 text-xs text-gray-600 whitespace-pre-wrap font-mono bg-gray-50 border rounded p-3 overflow-x-auto">
                {JSON.stringify(item.release_condition, null, 2)}
              </pre>
            </details>
          </section>
        )}
      </div>
    </div>
  );
};

// ── Blocked on Witness Table ──────────────────────────────────────

const BlockedOnWitnessTable: React.FC<{
  items: BlockedOnWitness[];
  onSelect: (item: BlockedOnWitness) => void;
  onReassignWitness: (item: BlockedOnWitness) => void;
}> = ({ items, onSelect, onReassignWitness }) => {
  if (items.length === 0) {
    return (
      <div className="text-gray-500 text-sm py-8 text-center bg-white rounded-lg shadow-sm">
        No steps awaiting your release
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {items.map(item => (
        <button
          key={item.execution_id}
          onClick={() => onSelect(item)}
          className="w-full text-left flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-colors shadow-sm"
        >
          <div className="flex items-center gap-3">
            <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${TIER_COLORS[item.consequence_tier] || 'bg-gray-100 text-gray-700'}`}>
              {item.consequence_tier}
            </span>
            <span className="text-sm font-medium text-gray-900">{item.dispatch_key}</span>
            <span className="text-sm text-gray-500">Step {item.sequence}: {item.step_name}</span>
            <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${GRADE_COLORS[item.grade || ''] || 'bg-gray-100 text-gray-600'}`}>
              {item.grade || 'ungraded'}
            </span>
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <span className="text-gray-600">{item.assigned_seat}</span>
            <span className={`font-medium ${item.hours_waiting > 24 ? 'text-red-700' : 'text-amber-700'}`}>
              {item.graded_at ? hoursSince(item.graded_at) + ' ago' : '--'}
            </span>
            <button
              onClick={(e) => { e.stopPropagation(); onReassignWitness(item); }}
              className="px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 rounded hover:bg-blue-100 transition-colors"
              title="Reassign escalation owner"
            >
              Reassign
            </button>
            <span className="text-gray-400">&rarr;</span>
          </div>
        </button>
      ))}
    </div>
  );
};

// ── Seat Mandates Table ────────────────────────────────────────────

const SeatMandatesTable: React.FC<{
  mandates: SeatMandate[];
}> = ({ mandates }) => {
  if (mandates.length === 0) {
    return (
      <div className="text-gray-500 text-sm py-8 text-center bg-white rounded-lg shadow-sm">
        No seat mandates configured
      </div>
    );
  }

  const withCeiling = mandates.filter(m => m.tier_ceiling !== 'production');
  const withoutCeiling = mandates.filter(m => m.tier_ceiling === 'production');

  return (
    <div className="space-y-4">
      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mandate Key</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Display Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Capability</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tier Ceiling</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Checker Profiles</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Active</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {mandates.map(m => (
                <tr key={m.mandate_id}>
                  <td className="px-4 py-3 text-sm font-mono text-gray-900">{m.mandate_key}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{m.display_name || '--'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{m.capability}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${TIER_COLORS[m.tier_ceiling] || 'bg-gray-100 text-gray-700'}`}>
                      {m.tier_ceiling}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {m.allowed_checker_profiles?.join(', ') || '--'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${m.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
                      {m.active ? 'yes' : 'no'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {withCeiling.length > 0 && withoutCeiling.length > 0 && (
        <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
          <span className="font-medium text-gray-700">{withCeiling.length}</span> seats have tier ceilings below production.{' '}
          <span className="font-medium text-gray-700">{withoutCeiling.length}</span> seats have production-level access.
          Ceilings are enforced at claim time -- a seat cannot exercise a step whose consequence_tier exceeds its ceiling.
        </div>
      )}
    </div>
  );
};

// ── Tab Badge ──────────────────────────────────────────────────────

const TabBadge: React.FC<{ count: number | null; color?: string }> = ({ count, color }) => {
  if (count === 0) return null;
  const colorClass = color || 'bg-gray-200 text-gray-700';
  return (
    <span className={`ml-1.5 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold rounded-full ${colorClass}`}>
      {count === null ? '?' : count}
    </span>
  );
};

// ── Main Dispatches Page ───────────────────────────────────────────

const Dispatches: React.FC = () => {
  const [dispatches, setDispatches] = useState<Dispatch[]>([]);
  const [escalations, setEscalations] = useState<Escalation[]>([]);
  const [stalledItems, setStalledItems] = useState<StalledDispatch[]>([]);
  const [blockedItems, setBlockedItems] = useState<BlockedOnWitness[]>([]);
  const [mandates, setMandates] = useState<SeatMandate[]>([]);
  const [loading, setLoading] = useState(true);
  const [queueState, setQueueState] = useState<Record<string, { error: string | null; updatedAt: string | null }>>({});
  const [actionError, setActionError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, total_pages: 0 });
  const [activeSummary, setActiveSummary] = useState({ assigned_active: 0, unrouted_active: 0 });
  const requestGeneration = useRef(0);
  const [activeTab, setActiveTab] = useState<TabKey>('dispatches');
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [seatFilter, setSeatFilter] = useState<string>('james');
  const [seats, setSeats] = useState<string[]>([]);
  const [selectedEscalation, setSelectedEscalation] = useState<Escalation | null>(null);
  const [showResolved, setShowResolved] = useState(false);
  const [assignTarget, setAssignTarget] = useState<Dispatch | null>(null);
  const [rejectTarget, setRejectTarget] = useState<Dispatch | null>(null);
  const [stalledAssignTarget, setStalledAssignTarget] = useState<StalledDispatch | null>(null);
  const [withdrawTarget, setWithdrawTarget] = useState<Dispatch | StalledDispatch | null>(null);
  const [selectedWitness, setSelectedWitness] = useState<BlockedOnWitness | null>(null);
  const [reassignWitnessTarget, setReassignWitnessTarget] = useState<BlockedOnWitness | null>(null);
  const [selectedDispatch, setSelectedDispatch] = useState<Dispatch | null>(null);
  const [completeTarget, setCompleteTarget] = useState<{ dispatch: Dispatch; allStepsPassed: boolean } | null>(null);

  const load = useCallback(async () => {
    const generation = ++requestGeneration.current;
    setLoading(true);
    const run = async <T,>(key: string, request: () => Promise<{ success: boolean; data?: T; error?: string }>, apply: (data: T) => void) => {
      try {
        const response = await request();
        if (!response.success || !response.data) throw new Error(response.error || 'Request failed');
        if (generation !== requestGeneration.current) return;
        apply(response.data);
        setQueueState(previous => ({ ...previous, [key]: { error: null, updatedAt: new Date().toISOString() } }));
      } catch (error) {
        if (generation !== requestGeneration.current) return;
        setQueueState(previous => ({ ...previous, [key]: {
          error: error instanceof Error ? error.message : 'Request failed', updatedAt: previous[key]?.updatedAt || null
        } }));
      }
    };
    const params: Record<string, string> = { page: String(page), limit: '50' };
    if (statusFilter !== 'all') params.status = statusFilter;
    if (seatFilter) params.assigned_seat = seatFilter;
    await Promise.all([
      run<ListResponse>('dispatches', () => dispatchesApi.list(params), data => {
        if (!data.pagination || !data.summary || !Array.isArray(data.seats)) {
          throw new Error('Queue summary is unavailable. Please refresh or contact support.');
        }
        const lastPage = Math.max(1, data.pagination.total_pages);
        if (page > lastPage) setPage(lastPage);
        setDispatches(data.records || []);
        setPagination(data.pagination);
        setActiveSummary(data.summary);
        setSeats(data.seats);
      }),
      run('escalations', () => dispatchesApi.escalations(showResolved ? { include_resolved: 'true' } : {}), data => setEscalations(data.escalations || [])),
      run('stalled', () => dispatchesApi.stalledDispatches(3), data => setStalledItems(data.stalled || [])),
      run('blocked_witness', () => dispatchesApi.blockedOnWitness(), data => setBlockedItems(data.blocked || [])),
      run('mandates', () => dispatchesApi.mandates(), data => setMandates(data.mandates || [])),
    ]);
    if (generation === requestGeneration.current) setLoading(false);
  }, [page, statusFilter, seatFilter, showResolved]);

  useEffect(() => {
    void load();
    return () => { requestGeneration.current++; };
  }, [load]);

  const performAction = async (body: Record<string, unknown>) => {
    const response = await dispatchesApi.action(body);
    if (!response.success) throw new Error(response.error || 'Action failed');
    setActionError(null);
    window.dispatchEvent(new Event('mindmeld:dispatches-updated'));
    await load();
  };

  const handleResolve = () => {
    setSelectedEscalation(null);
    window.dispatchEvent(new Event('mindmeld:dispatches-updated'));
    void load();
  };

  const handleAssignSeat = async (dispatchId: string, seat: string) => {
    await performAction({ action: 'assign_seat', dispatch_id: dispatchId, assigned_seat: seat });
  };

  const handleRejectDispatch = async (dispatchId: string, reason: string) => {
    await performAction({ action: 'reject', dispatch_id: dispatchId, reason });
  };

  const handleClearClaim = async (d: StalledDispatch) => {
    try {
      await performAction({ action: 'clear_claim', dispatch_id: d.dispatch_id, reason: 'admin clear -- stalled dispatch' });
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Clear claim failed');
    }
  };

  const handleWithdraw = async (dispatchId: string, reason: string) => {
    await performAction({ action: 'withdraw', dispatch_id: dispatchId, reason });
  };

  const handleComplete = async (dispatchId: string, completionRef: string) => {
    const body: Record<string, unknown> = { action: 'complete', dispatch_id: dispatchId };
    if (completionRef) body.completion_ref = completionRef;
    await performAction(body);
    setSelectedDispatch(null);
  };

  // Split dispatches into mine vs unrouted
  const mineDispatches = dispatches.filter(d => d.assigned_seat !== 'unassigned');
  const unroutedDispatches = dispatches.filter(d => d.assigned_seat === 'unassigned');

  const mineActiveCount = activeSummary.assigned_active;
  const unroutedActiveCount = activeSummary.unrouted_active;
  const openEscalationCount = escalations.filter(e => e.status === 'escalated').length;
  const stalledCount = stalledItems.length;
  const blockedCount = blockedItems.length;

  // W4: Badge = Mine + Unrouted + Stalled + Blocked-on-witness + open escalations
  const badgeTotal = mineActiveCount + unroutedActiveCount + stalledCount + blockedCount + openEscalationCount;

  // Keyboard shortcuts for unrouted items (only on dispatches tab)
  useEffect(() => {
    if (activeTab !== 'dispatches' || loading || queueState.dispatches?.error) return;
    if (assignTarget || rejectTarget || selectedEscalation || withdrawTarget || stalledAssignTarget) return;

    const handleKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLSelectElement) return;
      if (unroutedDispatches.length > 0) {
        const activeUnrouted = unroutedDispatches.filter(d => ACTIVE_STATUSES.includes(d.status));
        if (activeUnrouted.length === 0) return;
        if (e.key === 'a') {
          e.preventDefault();
          setAssignTarget(activeUnrouted[0]);
        }
        if (e.key === 'x') {
          e.preventDefault();
          setRejectTarget(activeUnrouted[0]);
        }
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [activeTab, loading, queueState, unroutedDispatches, assignTarget, rejectTarget, selectedEscalation, withdrawTarget, stalledAssignTarget]);

  const tabs: { key: TabKey; label: string; count: number; countColor: string }[] = [
    { key: 'dispatches', label: 'Dispatches', count: mineActiveCount + unroutedActiveCount, countColor: 'bg-blue-100 text-blue-800' },
    { key: 'stalled', label: 'Stalled', count: stalledCount, countColor: stalledCount > 0 ? 'bg-amber-100 text-amber-800' : 'bg-gray-200 text-gray-700' },
    { key: 'blocked_witness', label: 'Awaiting your release', count: blockedCount, countColor: blockedCount > 0 ? 'bg-red-100 text-red-800' : 'bg-gray-200 text-gray-700' },
    { key: 'escalations', label: 'Escalations', count: openEscalationCount, countColor: openEscalationCount > 0 ? 'bg-amber-100 text-amber-800' : 'bg-gray-200 text-gray-700' },
    { key: 'mandates', label: 'Seat Mandates', count: 0, countColor: '' },
  ];

  return (
    <div>
      {/* Header with badge */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">Dispatches</h1>
            {!loading && !Object.values(queueState).some(state => state.error) && badgeTotal > 0 && (
              <span className="inline-flex items-center justify-center px-2.5 py-1 text-sm font-bold text-white bg-blue-600 rounded-full">
                {badgeTotal}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {loading ? 'Refreshing queues…' : queueState.dispatches?.error ? 'Active dispatch count unavailable' :
              `${mineActiveCount} active ${seatFilter === 'james' ? 'for you' : seatFilter ? `for ${seatFilter}` : 'across all seats'} · ${unroutedActiveCount} unrouted`}
          </p>
          {actionError && <p role="alert" className="text-sm text-red-700">{actionError}</p>}
          <button onClick={() => void load()} disabled={loading} className="text-sm text-blue-700 disabled:opacity-50">Refresh queues</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-5">
        <nav className="-mb-px flex space-x-6">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
              <TabBadge count={loading || queueState[tab.key]?.error ? null : tab.count} color={tab.countColor} />
            </button>
          ))}
        </nav>
      </div>

      {loading ? (
        <div className="text-gray-500 text-sm py-8 text-center">Loading...</div>
      ) : queueState[activeTab]?.error ? (
        <div role="alert" className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p>Could not load {tabs.find(tab => tab.key === activeTab)?.label}: {queueState[activeTab].error}</p>
          <p className="text-sm">{queueState[activeTab].updatedAt
            ? `Last successful refresh: ${new Date(queueState[activeTab].updatedAt!).toLocaleString()}. Results are unavailable until refreshed.`
            : 'This queue has not loaded successfully. Its count is unknown.'}</p>
          <button onClick={() => void load()} className="mt-2 text-blue-700">Retry queue</button>
        </div>
      ) : (
        <>
          <p className="text-xs text-gray-500 mb-3">Last successful refresh: {queueState[activeTab]?.updatedAt
            ? new Date(queueState[activeTab].updatedAt!).toLocaleString() : 'Not yet loaded'}</p>
          {/* ── Dispatches Tab ──────────────────────────────────────── */}
          {activeTab === 'dispatches' && (
            <>
              {selectedDispatch ? (
                <div className="mb-5">
                  <DispatchDetail
                    dispatch={selectedDispatch}
                    onClose={() => setSelectedDispatch(null)}
                    onReassign={d => setAssignTarget(d)}
                    onWithdraw={d => setWithdrawTarget(d)}
                    onComplete={(d, allStepsPassed) => setCompleteTarget({ dispatch: d, allStepsPassed })}
                    onRefresh={() => { setSelectedDispatch(null); window.dispatchEvent(new Event('mindmeld:dispatches-updated')); void load(); }}
                  />
                </div>
              ) : (
                <>
                  {/* Dispatch Filters */}
                  <div className="flex gap-3 mb-4">
                    <select
                      value={statusFilter}
                      onChange={e => { setPage(1); setStatusFilter(e.target.value); }}
                      aria-label="Dispatch status"
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white"
                    >
                      <option value="all">All</option>
                      <option value="active">Active (pending/claimed/in_progress/blocked)</option>
                      <option value="pending">Pending</option>
                      <option value="claimed">Claimed</option>
                      <option value="in_progress">In Progress</option>
                      <option value="blocked">Blocked</option>
                      <option value="complete">Complete</option>
                      <option value="superseded">Superseded</option>
                      <option value="rejected">Rejected</option>
                      <option value="withdrawn">Withdrawn</option>
                      <option value="draft">Draft</option>
                    </select>
                    <select
                      value={seatFilter}
                      onChange={e => { setPage(1); setSeatFilter(e.target.value); }}
                      aria-label="Dispatch seat"
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white"
                    >
                      <option value="james">Mine</option>
                      <option value="">All seats</option>
                      {seats.filter(s => s !== 'james').map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>

                  <nav aria-label="Dispatch pagination" className="flex items-center gap-3 mb-4 text-sm">
                    <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 border rounded disabled:opacity-40">Previous page</button>
                    <span>Page {pagination.page} of {Math.max(1, pagination.total_pages)} · {pagination.total} matching dispatches</span>
                    <button disabled={page >= pagination.total_pages} onClick={() => setPage(p => p + 1)} className="px-3 py-1 border rounded disabled:opacity-40">Next page</button>
                  </nav>
                  <div className="space-y-6">
                    {/* Unrouted Queue */}
                    {unroutedDispatches.length > 0 && (
                      <section>
                        <div className="flex items-center gap-2 mb-3">
                          <h2 className="text-lg font-semibold text-gray-900">Unrouted</h2>
                          <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold text-amber-800 bg-amber-100 rounded-full">
                            {unroutedActiveCount}
                          </span>
                          <span className="text-xs text-gray-400 ml-2">
                            <kbd className="px-1.5 py-0.5 bg-gray-100 border rounded text-gray-600">a</kbd> assign
                            <span className="mx-1">/</span>
                            <kbd className="px-1.5 py-0.5 bg-gray-100 border rounded text-gray-600">x</kbd> reject
                          </span>
                        </div>
                        <DispatchTable
                          dispatches={unroutedDispatches}
                          showActions
                          onAssignSeat={d => setAssignTarget(d)}
                          onReject={d => setRejectTarget(d)}
                          onSelect={d => setSelectedDispatch(d)}
                        />
                      </section>
                    )}

                    {/* Mine */}
                    <section>
                      <div className="flex items-center gap-2 mb-3">
                        <h2 className="text-lg font-semibold text-gray-900">
                          {seatFilter === 'james' ? 'Mine' : seatFilter ? seatFilter : 'All Seats'}
                        </h2>
                        <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold text-blue-800 bg-blue-100 rounded-full">
                          {mineActiveCount}
                        </span>
                      </div>
                      {mineDispatches.length === 0 ? (
                        <div className="text-gray-500 text-sm py-8 text-center">No dispatches match filters</div>
                      ) : (
                        <DispatchTable
                          dispatches={mineDispatches}
                          onSelect={d => setSelectedDispatch(d)}
                        />
                      )}
                    </section>
                  </div>
                </>
              )}
            </>
          )}

          {/* ── Stalled Tab ─────────────────────────────────────────── */}
          {activeTab === 'stalled' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Stalled Dispatches</h2>
                  <p className="text-xs text-gray-500 mt-1">
                    Dispatches claimed or in progress with no step activity for 3+ days. Sorted oldest first.
                  </p>
                </div>
              </div>
              <StalledTable
                items={stalledItems}
                onClearClaim={handleClearClaim}
                onReassignSeat={d => setStalledAssignTarget(d)}
                onWithdraw={d => setWithdrawTarget(d)}
              />
            </div>
          )}

          {/* ── Blocked on Witness Tab ──────────────────────────────── */}
          {activeTab === 'blocked_witness' && (
            <div>
              {selectedWitness ? (
                <div className="mb-5">
                  <WitnessDetail
                    item={selectedWitness}
                    onRelease={() => { setSelectedWitness(null); window.dispatchEvent(new Event('mindmeld:dispatches-updated')); void load(); }}
                    onClose={() => setSelectedWitness(null)}
                  />
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">Awaiting your release</h2>
                      <p className="text-xs text-gray-500 mt-1">
                        These steps require a human release because of their consequence tier or grader mode. This is not the machine-witness corroboration queue.
                        Click a step to review the report and release.
                      </p>
                    </div>
                  </div>
                  <BlockedOnWitnessTable
                    items={blockedItems}
                    onSelect={item => setSelectedWitness(item)}
                    onReassignWitness={item => setReassignWitnessTarget(item)}
                  />
                </>
              )}
            </div>
          )}

          {/* ── Escalations Tab ─────────────────────────────────────── */}
          {activeTab === 'escalations' && (
            <div>
              {selectedEscalation ? (
                <div className="mb-5">
                  <EscalationDetail
                    key={selectedEscalation.execution_id}
                    esc={selectedEscalation}
                    onResolve={handleResolve}
                    onClose={() => setSelectedEscalation(null)}
                  />
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">Escalations</h2>
                      <p className="text-xs text-gray-500 mt-1">
                        {openEscalationCount} open escalation{openEscalationCount !== 1 ? 's' : ''} awaiting human verdict
                      </p>
                    </div>
                    <label className="flex items-center gap-1.5 text-xs text-gray-500">
                      <input
                        type="checkbox"
                        checked={showResolved}
                        onChange={e => setShowResolved(e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      Show resolved
                    </label>
                  </div>
                  {escalations.length === 0 ? (
                    <div className="text-gray-500 text-sm py-8 text-center bg-white rounded-lg shadow-sm">
                      No escalations
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {escalations.map(esc => (
                        <button
                          key={esc.execution_id}
                          onClick={() => setSelectedEscalation(esc)}
                          className="w-full text-left flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-colors shadow-sm"
                        >
                          <div className="flex items-center gap-3">
                            <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                              esc.status === 'escalated' ? 'bg-red-100 text-red-800' :
                              esc.human_verdict === 'proceed' ? 'bg-green-100 text-green-800' :
                              esc.human_verdict === 'reject' ? 'bg-red-100 text-red-700' :
                              'bg-amber-100 text-amber-800'
                            }`}>
                              {esc.status === 'escalated' ? 'open' : esc.human_verdict}
                            </span>
                            <span className="text-sm font-medium text-gray-900">{esc.dispatch_key}</span>
                            <span className="text-sm text-gray-500">Step {esc.sequence}: {esc.step_name}</span>
                            {isQuestionShaped(esc.config) && (
                              <span className="inline-flex px-1.5 py-0.5 text-xs rounded bg-purple-100 text-purple-700">question</span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span className={`font-medium ${GRADE_COLORS[esc.grade || '']?.split(' ')[0] || 'text-gray-500'}`}>
                              {esc.grade || '--'}
                            </span>
                            {esc.graded_at && <span>{hoursSince(esc.graded_at)} ago</span>}
                            <span className="text-gray-400">&rarr;</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── Seat Mandates Tab ───────────────────────────────────── */}
          {activeTab === 'mandates' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Seat Mandates</h2>
                  <p className="text-xs text-gray-500 mt-1">
                    Current mandate tier ceilings. Read-only view. Ceilings are enforced at claim time.
                  </p>
                </div>
              </div>
              <SeatMandatesTable mandates={mandates} />
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {assignTarget && (
        <AssignSeatModal
          dispatch={assignTarget}
          onAssign={handleAssignSeat}
          onClose={() => setAssignTarget(null)}
        />
      )}
      {rejectTarget && (
        <RejectModal
          dispatch={rejectTarget}
          onReject={handleRejectDispatch}
          onClose={() => setRejectTarget(null)}
        />
      )}
      {stalledAssignTarget && (
        <AssignSeatModal
          dispatch={stalledAssignTarget}
          title="Reassign Seat"
          onAssign={handleAssignSeat}
          onClose={() => setStalledAssignTarget(null)}
        />
      )}
      {withdrawTarget && (
        <WithdrawModal
          dispatch={withdrawTarget}
          onWithdraw={handleWithdraw}
          onClose={() => setWithdrawTarget(null)}
        />
      )}
      {completeTarget && (
        <CompleteModal
          dispatch={completeTarget.dispatch}
          allStepsPassed={completeTarget.allStepsPassed}
          onComplete={handleComplete}
          onClose={() => setCompleteTarget(null)}
        />
      )}
      {reassignWitnessTarget && (
        <AssignSeatModal
          dispatch={{
            dispatch_id: reassignWitnessTarget.dispatch_id,
            dispatch_key: reassignWitnessTarget.dispatch_key,
          } as Dispatch}
          title="Reassign Witness Seat"
          onAssign={handleAssignSeat}
          onClose={() => setReassignWitnessTarget(null)}
        />
      )}
    </div>
  );
};

export default Dispatches;
